<template>
  <DialogTemplate
    align="center"
    class="dialog"
    :show-controls="true"
    v-bind="props"
    @close="emit('close')"
  >
    <template #header>
      <div class="">
        <img
          class="badge"
          :src="imgUrl"
          :alt="title"
          :style="style"
          @load="handleLoaded"
        />
        <span class="title">{{ title }}</span>
        <span v-if="subIitle" class="sub-title">{{ subIitle }}</span>
      </div>
    </template>

    <p>{{ label }}</p>
    <template #footer>
      <div class="footer">
        <div v-if="buttonTrophies" class="actions">
          <EffectParticles />
          <ButtonBase
            rounded
            theme="secondary"
            variant="tonal"
            stroke="n"
            class="button-trophies"
            @click="handleClickTrophies"
          >
            {{ $t('trophies.popin.button.trophies') }}
          </ButtonBase>
        </div>
        <div v-else-if="completed" class="completed">
          <icon class="icon-done" name="trophyDone"></icon>
          {{ $t('trophies.popin.alreadyObtained') }}
        </div>
        <div v-else-if="ratioProgress > 0" class="progress">
          <span class="progress-label">{{ labelProgress }}</span>
        </div>

        <ButtonBase
          v-if="!completed && redirect?.path"
          rounded
          theme="secondary"
          variant="tonal"
          stroke="n"
          :to="redirect.path"
          @click="() => dialog.close()"
        >
          {{ buttonDeepLinkLabel }}
        </ButtonBase>
      </div>
    </template>
  </DialogTemplate>
</template>
<script lang="ts" setup>
interface Props {
  title: string;
  subIitle?: string;
  label: string;
  iconHeader?: string | null;
  closeTimer?: number;
  closeClickOutside?: boolean;
  position?: 'center' | 'top-right';
  imgUrl: string;
  ratioProgress?: number;
  progress: number;
  threshold?: number;
  buttonTrophies?: boolean;
  buttonDeepLinkLabel?: string;
  buttonDeepLinkValue?: string;
  onClose;
}

const props = defineProps({
  title: null,
  subIitle: null,
  label: null,
  iconHeader: null,
  closeTimer: { default: -1 },
  closeClickOutside: { type: Boolean, default: true },
  position: { default: 'center' },
  imgUrl: null,
  ratioProgress: { default: 0 },
  progress: null,
  threshold: { default: 1 },
  buttonTrophies: { type: Boolean, default: false },
  buttonDeepLinkLabel: null,
  buttonDeepLinkValue: null,
  onClose: null
});
const emit = defineEmits(["close"]);

const dialog = useDialogStore();

const completed = computed(() => props.ratioProgress === 1);

const { style, scale } = useScaleEffect(0, true);

const { t } = useI18n();

const labelProgress = computed(() => {
  return t('trophies.popin.progression', {
    label: `${props.progress} / ${props.threshold}`,
  });
});

const handleClickTrophies = () => {
  navigateTo('/gaming#trophees');
  dialog.close();
  emit('close');
};

const handleLoaded = () => {
  scale.value = 1;
};

const buttonDeepLinkLabel = computed(
  () => props.buttonDeepLinkLabel || "Let's go !"
);

const redirect = await useDeepLinkParser(props.buttonDeepLinkValue);
</script>
<style lang="scss" scoped>
.dialog {
  position: relative;
  @include above('l') {
    max-width: 40rem;
  }
}
.badge {
  position: absolute;
  top: 0;
  left: 50%;
  width: auto;
  height: 10rem;
  transform: translate(-50%, -50%);
}
.title {
  display: block;
  margin-top: 4rem;
}

.sub-title {
  @include font('m', '500');
}

.completed,
.progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap_n);
  color: var(--color_success);
  .icon-done {
    padding: 0.1rem;
    border-radius: 50%;
    background: var(--color_success);
    color: var(--color_neutral_50);
  }
}
.progress {
  color: var(--color_secondary);
  @include font('n', 600);
}
.completed {
  @include font('n', 600);

  color: var(--color_success);
}
.footer {
  display: flex;
  flex-direction: column;
  gap: var(--gap_n);
}
.button-trophies {
  width: 100%;
}
</style>
